import React, { useState, useEffect, JSX } from "react";
import { Modal, IconButton, PrimaryButton, Dropdown, IDropdownOption } from "@fluentui/react";
import DOMPurify from "dompurify";
import { getGdprApiV1, GDPRContent, GDPRResponse } from "../../api";
import { useGetToken } from "../../authConfig";
import "./GDPRModal.css";

interface GDPRModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const GDPRModal: React.FC<GDPRModalProps> = ({ isOpen, onClose }) => {
    const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
    const [content, setContent] = useState<GDPRContent>({
        title: "Loading...",
        text: "Loading GDPR content...",
        language: "en"
    });
    const [availableLanguages, setAvailableLanguages] = useState<IDropdownOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [gdprData, setGdprData] = useState<GDPRResponse | null>(null);

    const token = useGetToken();

    useEffect(() => {
        if (isOpen && token) {
            fetchGDPRContent();
        }
    }, [isOpen, token]);

    useEffect(() => {
        if (gdprData && selectedLanguage) {
            setContent(gdprData.content[selectedLanguage] || gdprData.content["en"]);
        }
    }, [selectedLanguage, gdprData]);

    const fetchGDPRContent = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await getGdprApiV1(token);
            setGdprData(response);

            const languageOptions = response.availableLanguages.map(lang => ({
                key: lang.code,
                text: lang.name,
                data: { flag: lang.flag }
            }));

            setAvailableLanguages(languageOptions);
            setContent(response.content[selectedLanguage] || response.content["en"]);
        } catch (err: any) {
            console.error("Failed to fetch GDPR content:", err);
            setError("Failed to load GDPR content. Please try again.");
            // Fallback content
            setContent({
                title: "GDPR Policy",
                text: "Unable to load GDPR content at this time.",
                language: "en"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleLanguageChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            setSelectedLanguage(option.key as string);
        }
    };

    const renderLanguageOption = (option?: IDropdownOption): JSX.Element => {
        return (
            <div className="gdpr-language-option">
                <span>{option?.data?.flag}</span>
                <span>{option?.text}</span>
            </div>
        );
    };

    const renderLanguageTitle = (options?: IDropdownOption[]): JSX.Element => {
        const selectedOption = options?.find(option => option.key === selectedLanguage);
        return (
            <div className="gdpr-language-option">
                <span>{selectedOption?.data?.flag}</span>
                <span>{selectedOption?.text}</span>
            </div>
        );
    };

    const sanitizedText = DOMPurify.sanitize(content.text);
    
    return (
        <Modal isOpen={isOpen} onDismiss={onClose} isBlocking={true} containerClassName="gdpr-modal-container">
            <div className="gdpr-modal-content">
                <div className="gdpr-modal-header">
                    <h2 className="gdpr-modal-title">{content.title}</h2>
                    {/* <IconButton iconProps={{ iconName: "Cancel" }} onClick={onClose} ariaLabel="Close modal" /> */}
                </div>

                <div className="gdpr-language-selector">
                    <Dropdown
                        label="Language"
                        options={availableLanguages}
                        selectedKey={selectedLanguage}
                        onChange={handleLanguageChange}
                        onRenderOption={renderLanguageOption}
                        onRenderTitle={renderLanguageTitle}
                        className="gdpr-dropdown"
                    />
                </div>

                <div className="gdpr-content-container">
                    {isLoading ? (
                        <div>Loading GDPR content...</div>
                    ) : error ? (
                        <div style={{ color: "red" }}>{error}</div>
                    ) : (
                        <div dangerouslySetInnerHTML={{ __html: sanitizedText }} />
                    )}
                </div>

                <div className="gdpr-modal-actions">
                    <PrimaryButton text="Accept" onClick={onClose} />
                </div>
            </div>
        </Modal>
    );
};

export default GDPRModal;
